import React from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { Form, Field } from 'react-final-form';
import { Link } from "react-router-dom";
import { Row, Col, ButtonGroup, Button, Card, CardHeader, CardBody, CardTitle, CardText } from 'reactstrap';
import { FormGroup, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { User } from '../../../models/';
import { Utils, isSuperAdmin } from '../../../services/';
import { Role, PageTitle, InvoiceDetails } from '../../../components';
import { loadUser } from '../../../redux/modules';
import Icon from '@mdi/react';
import {
    mdiAccountSchoolOutline,
    mdiAccountCircleOutline,
    mdiCardAccountMailOutline,
    mdiCardAccountPhoneOutline,
    mdiCommentAccountOutline,
} from '@mdi/js';

import './UserForm.css';

const UserForm = props => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const { onSubmit, isCreation } = props;
    const { user, isLoading }      = useSelector(state => state.users);

    const reloadUser = () => {
        dispatch(loadUser(user.id));
    };

    return (
        <Form
            initialValues={ user }
            onSubmit={ values => { onSubmit(values); }}
            validate={ values => { console.log('validate', values); }}
            render={({ handleSubmit, pristine, form, submitting }) => (
                <form className="form" onSubmit={handleSubmit}>
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountCircleOutline} size={1.5} />&nbsp;
                                        {t('users.profile')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="firstname" sm={3}>{t('users.firstname')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="firstname" id="firstname" autoComplete="given-name" placeholder={t('users.firstname_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="lastname" sm={3}>{t('users.lastname')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="lastname" id="lastname" autoComplete="family-name" placeholder={t('users.lastname_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="gender" sm={3}>{t('users.gender')}</Label>
                                            <Col sm={9}>
                                                <Field required component="select" className="form-control" type="text" name="gender" id="gender">
                                                    {
                                                        User.genders().map(g => <option key={g} value={g}>{g}</option>)
                                                    }
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        <Role has="admin">
                                            <FormGroup row>
                                                <Label for="role" sm={3}>{t('users.role')}</Label>
                                                <Col sm={9}>
                                                    <Field required component="select" className="form-control" type="text" name="role" id="role" placeholder="None">
                                                        {isCreation &&
                                                            <option value="" disabled selected>{t("users.role_placeholder")}</option>}
                                                        {
                                                            Utils.roles(/*role*/null, /*no_super_admin*/!isSuperAdmin()).map((r) => <option key={r.role} value={r.role}>{r.label}</option>)
                                                        }
                                                    </Field>
                                                </Col>
                                            </FormGroup>
                                        </Role>
                                        <FormGroup row>
                                            <Label for="new-password" sm={3}>{t('users.password')}</Label>
                                            <Col sm={9}>
                                                <Field required={ isCreation } component="input" className="form-control" type="password" name="new-password" id="new-password" autoComplete="new-password" placeholder={t('users.password_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="new-password-confirmation" sm={3}>{t('users.password_confirmation')}</Label>
                                            <Col sm={9}>
                                                <Field required={ isCreation } component="input" className="form-control" type="password" name="new-password-confirmation" id="new-password-confirmation" autoComplete="new-password" placeholder={t('users.password_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        { !isCreation && (
                                        <FormGroup row>
                                            <Label for="enrolment.status" sm={3}>{t('users.student_profile')}</Label>
                                            <Col sm={9}>
                                                { !user?.student?.id && (
                                                <>
                                                    <CardTitle tag="em">
                                                        { t('users.user_has_no_student_profile') }
                                                    </CardTitle>
                                                    <Button tag={Link} to={`/students/new/from/${user?.id}`} color="info">
                                                        <Icon path={mdiAccountSchoolOutline} size={1} />&nbsp;
                                                        { t('users.add_student_profile') }
                                                    </Button>
                                                </>
                                                )}
                                                { user?.student?.id && (
                                                <>
                                                    <CardTitle tag="em">
                                                        { t('users.user_has_student_profile') }
                                                    </CardTitle>
                                                    <Button tag={Link} to={`/students/edit/${user?.student?.id}`} color="info">
                                                        <Icon path={mdiAccountSchoolOutline} size={1} />&nbsp;
                                                        { t('users.show_student_profile') }
                                                    </Button>
                                                </>
                                                )}
                                            </Col>
                                        </FormGroup>
                                        )}
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCardAccountMailOutline} size={1.5} />&nbsp;
                                        {t('users.address')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="address" sm={3}>{t('users.address')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="address" id="address" autoComplete="address-line1" placeholder={t('users.address_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="zip" sm={3}>{t('users.zip')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="zip" id="zip" autoComplete="postal-code" placeholder={t('users.zip_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="city" sm={3}>{t('users.city')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="city" id="city" autoComplete="address-level2" placeholder={t('users.city_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="country" sm={3}>{t('users.country')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="country" id="country" autoComplete="country-name" placeholder={t('users.country_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="row-flex">
                        <Col>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCardAccountPhoneOutline} size={1.5} />&nbsp;
                                        {t('users.contact')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="email" sm={3}>{t('users.email')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="email" name="email" autoComplete="email" placeholder={t('users.email_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="phone" sm={3}>{t('users.mobile_phone')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="phone" id="phone" autoComplete="tel-mobile" placeholder={t('users.phone_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col>
                            <Card color="light">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCommentAccountOutline} size={1.5} />&nbsp;
                                        {t('users.other_informations')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="remarks" sm={3}>{t('users.remarks')}</Label>
                                            <Col sm={9}>
                                                <Field component="textarea" rows={ 6 } className="form-control" name="remarks" id="remarks" />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    { !isCreation && user?.children?.length > 0 && (
                        <>
                            <PageTitle title={t('app.enrolment_fees')} />
                            {user?.student && (
                                <Row className="main row-flex">
                                { user?.student?.invoices?.map((invoice, i) => (
                                    <Col sm={12} lg={12}>
                                        <Card color="light" className="h-100">
                                            <CardHeader>
                                                <CardTitle tag="h3">
                                                    <Icon path={mdiAccountSchoolOutline} size={1} />&nbsp;
                                                    { t('students.invoices_of', {name: user?.student.fullname}) }
                                                </CardTitle>
                                            </CardHeader>
                                            <CardBody>
                                                <InvoiceDetails
                                                    paymentName={`${user?.student.id}_new_payment`}
                                                    invoice={invoice}
                                                    className="h-100"
                                                    pristine={pristine}
                                                    submitting={submitting}
                                                    isLoadingOne={isLoading}
                                                    onPaymentDeleted={reloadUser}
                                                />
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))}
                                </Row>
                            )}
                            <Row className="main row-flex">
                                {user?.children?.map(student => (
                                    <Col sm={12} lg={12}>
                                        <Row className="main row-flex">
                                        { student?.invoices?.map((invoice, i) => (
                                            <Col sm={12} lg={12}>
                                                <Card color="light" className="h-100">
                                                    <CardHeader>
                                                        <CardTitle tag="h3">
                                                            <Icon path={mdiAccountSchoolOutline} size={1} />&nbsp;
                                                            { t('students.invoices_of', {name: student.fullname}) }
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <InvoiceDetails
                                                            paymentName={`${student.id}_new_payment`}
                                                            invoice={invoice}
                                                            className="h-100"
                                                            pristine={pristine}
                                                            submitting={submitting}
                                                            isLoadingOne={isLoading}
                                                            onPaymentDeleted={reloadUser}
                                                        />
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                        </Row>
                                    </Col>
                                ))}
                            </Row>
                        </>
                    )}
                    <Row className="main">
                        <Col className="text-center">
                            <ButtonGroup>
                                <Button tag={Link} to={ -1 } color="warning">
                                    <span><i className="fa fa-arrow-left"></i>&nbsp;{t('common.back')}</span>
                                </Button>
                                <Button type="submit" color="success" disabled={pristine || submitting || isLoading}>
                                    <span>
                                        <i className={`fa ${ isLoading ? 'fa-spinner fa-spin' : 'fa fa-save'}`}></i>&nbsp;{t("common.save")}
                                    </span>
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </form>
        )} />
    );
}

export default UserForm;
